import { useCallback, useState } from "react";
import {
  IAddOrUpdateLicensePayload,
  IBaseLicenseDetails,
  ILicensingDetail,
} from "../../../common/types/mobileLicensingTypes";
import { useDispatch, useSelector } from "react-redux";
import { useSpinner } from "../../../customHooks";
import {
  addOrManageMobileLicense,
  disableMobileLicense,
  selector as mobileLicensingApi,
} from "./mobileLicensingSlice";
import useNotification from "../../notification/useNotification";
import { getOrganisationLicenses } from "../licensingModal/licensingSlice";
import { ACTION } from "../../../common/constants";
export const useMobileLicensing = (
  license: ILicensingDetail,
  orgId: string,
  onClose: () => void
) => {
  const mobilelicenseDetailsKey : string =
    Object.keys(license).find((key) => key !== "type" && key !== "name") ?? "";

  const mobileLicenseDetails = license[
    mobilelicenseDetailsKey
  ] as IBaseLicenseDetails;
  const getDate = (event: string) => {
    if (!event) return null;
    const dateObject: Date = new Date(event);
    const day: string = dateObject.getDate().toString().padStart(2, "0");
    const month: string = (dateObject.getMonth() + 1)
      .toString()
      .padStart(2, "0");
    const year: number = dateObject.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const [show, setShow] = useState<boolean>(false);
  const [licenseCount, setLicenseCount] = useState<string | number>(
    mobileLicenseDetails?.totalLicenseCount || ""
  );
  const { showSuccessNotification, showWarningNotification } =
    useNotification();
  const [expireDate, setExpireDate] = useState<Date>(
    mobileLicenseDetails?.expireDate
      ? new Date(mobileLicenseDetails?.expireDate)
      : null
  );
  const [expireDateString, setExpireDateString] = useState<string>(
    getDate(mobileLicenseDetails?.expireDate)
  );
  const [isManageConfirmationVisible, setIsManageConfirmationVisible] =
  useState<boolean>(false);
  const mobileLicensingApiResponse = useSelector(mobileLicensingApi);
  useSpinner(mobileLicensingApiResponse.status);
  const dispatch: any = useDispatch();

  const usedLicenseCount = mobileLicenseDetails?.usedLicenseCount || 0;
  const enteredLicenseCount = Number(licenseCount) || 0;
  const shouldShowManageWarning = enteredLicenseCount < usedLicenseCount;
  const licensesToBeRevoked = shouldShowManageWarning
    ? usedLicenseCount - enteredLicenseCount
    : 0;

  const handleExpireDateChange = (event) : void => {
    setExpireDate(event);
    setExpireDateString(getDate(event));
  };

  
  const resetManageConfirmationState = () : void => {
    setIsManageConfirmationVisible(false);
  };

  const handleClose = useCallback(() : void => {
    resetManageConfirmationState();
    onClose();
  }, [onClose]);

  const validateManageOrEnableForm = ():boolean => {
    if (Number(licenseCount) < 1) {
      showWarningNotification("Please provide a valid number of licenses.");
      return false;
    }

    if (!expireDate) {
      showWarningNotification("Please select an expiry date.");
      return false;
    }

    const selectedDate = new Date(expireDate);
    selectedDate.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      showWarningNotification("Expiry date cannot be a past date.");
      return false;
    }

    return true;
  };

  const handleManageContinue = () : void => {
    const isFormValid = validateManageOrEnableForm();
    if (!isFormValid) return;

    setIsManageConfirmationVisible(true);
  };

  const handleManageBack = () : void => {
    setIsManageConfirmationVisible(false);
  };

  const handleShow = useCallback(() : void => setShow(true), [setShow]);
  
  const handleAddorManageLicense = () : void => {
    const payload: IAddOrUpdateLicensePayload = {
      orgId: orgId,
      totalLicenseCount: Number(licenseCount),
      expireDate: expireDateString,
    };
    dispatch(addOrManageMobileLicense(payload)).then((response) => {
      if (response.meta.requestStatus === "fulfilled") {
        showSuccessNotification(response.payload);
        dispatch(getOrganisationLicenses(orgId));
      } else {
        showWarningNotification(response.payload || "Something went wrong");
      }
      handleClose();
    });
  };
  const handleDisableMobileLicense = () : void => {
    dispatch(disableMobileLicense(mobileLicenseDetails?.orgId))
      .then((response) => {
        if (response.meta.requestStatus === "fulfilled") {
          showSuccessNotification(response.payload);
          dispatch(getOrganisationLicenses(orgId));
        } else {
          showWarningNotification(
            response.payload?.response?.data?.message || "Something went wrong"
          );
        }
      })
      .finally(() => {
        handleClose();
      });
  };

  const handleMobileLicenseAction = (action: string) : (() => void) => {
    if (action === ACTION.ENABLE) {
      return () => {
        const isFormValid = validateManageOrEnableForm();
        if (!isFormValid) return;

        handleAddorManageLicense();
      };
    }

    if (action === ACTION.MANAGE) {
      return isManageConfirmationVisible
        ? handleAddorManageLicense
        : handleManageContinue;
    }

    if (action === ACTION.DISABLE) {
      return handleDisableMobileLicense;
    }

    return () => {};
  };

  return {
    show,
    handleClose,
    handleShow,
    licenseCount,
    usedLicenseCount: mobileLicenseDetails?.usedLicenseCount || 0,
    setLicenseCount,
    expireDate,
    setExpireDate,
    handleExpireDateChange,
    handleAddorManageLicense,
    handleDisableMobileLicense,
    handleMobileLicenseAction,
    isManageConfirmationVisible,
    handleManageBack,
    shouldShowManageWarning,
    licensesToBeRevoked,
  };
};
